// src/components/mdx/client/BeforeAfterImage.client.tsx

/**
 * Blog Image Components
 * 
 * A collection of components for displaying images in blog posts with various
 * layouts, animations, and interactive features. These components provide
 * consistent styling and behavior while supporting different presentation needs.
 * 
 * The components include:
 * - BlogImage: Standard animated image with caption
 * - EnhancedBlogImage: Image with hover effects and better image handling
 * - ImageGrid: Responsive grid layout for multiple images
 * - ImageCarousel: Scrollable carousel for multiple images
 * - ImageWithText: Image with accompanying text in various layouts
 * - BeforeAfterImage: Interactive comparison of two images
 */

'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import { cn } from '~/lib/utils'

/**
 * Interface for accessibility attributes
 * 
 * @interface AriaProps
 * @typedef {Object} AriaProps
 * @property {string} [label] - Accessible label for the image
 * @property {string} [describedBy] - ID of element that describes the image
 */
interface AriaProps {
  /** Accessible label for the image */
  label?: string
  /** ID of element that describes the image */
  describedBy?: string
}

/**
 * Props for the BlogImage component
 * 
 * @interface BlogImageProps
 * @typedef {Object} BlogImageProps
 * @property {string} src - Source URL of the image
 * @property {string} alt - Alternative text for the image
 * @property {string} [caption] - Optional caption text
 * @property {number} [width=1200] - Width of the image in pixels
 * @property {number} [height=630] - Height of the image in pixels
 * @property {boolean} [priority=false] - Whether to prioritize loading this image
 * @property {string} [className] - Additional CSS classes
 * @property {string} [sizes] - Responsive size attribute for the image
 * @property {AriaProps} [aria] - Accessibility attributes (label {string}, describedBy {string})
 * @property {string} [customAspectRatio] - Custom aspect ratio for the image (e.g. "aspect-[2/1]")
 */
export interface BlogImageProps {
  /** Source URL of the image */
  src: string
  /** Alternative text for the image */
  alt: string
  /** Optional caption text */
  caption?: string
  /** Width of the image in pixels */
  width?: number
  /** Height of the image in pixels */
  height?: number
  /** Whether to prioritize loading this image */
  priority?: boolean
  /** Additional CSS classes */
  className?: string
  /** Responsive size attribute for the image */
  sizes?: string
  /** Accessibility attributes */
  aria?: AriaProps
  /** Custom aspect ratio */
  customAspectRatio?: string
}


/**
 * Props for the BeforeAfterImage component
 * 
 * @interface BeforeAfterImageProps
 * @typedef {Object} BeforeAfterImageProps
 * @property {BlogImageProps} beforeImage - Image to show in the "before" state
 * @property {BlogImageProps} afterImage - Image to show in the "after" state
 * @property {string} [className] - Additional CSS classes
 * @property {string} [beforeLabel='Before'] - Label for the "before" state
 * @property {string} [afterLabel='After'] - Label for the "after" state
 */
export interface BeforeAfterImageProps {
  /** Image to show in the "before" state */
  beforeImage: BlogImageProps
  /** Image to show in the "after" state */
  afterImage: BlogImageProps
  /** Additional CSS classes */
  className?: string
  /** Label for the "before" state */
  beforeLabel?: string
  /** Label for the "after" state */
  afterLabel?: string
}

/**
 * Interactive comparison of two images with a draggable divider
 * 
 * @function BeforeAfterImage
 * @param {BeforeAfterImageProps} props - Component properties
 * @returns {JSX.Element} Rendered component
 * @example
 * ```tsx
 * <BeforeAfterImage
 *   beforeImage={{ src: "/images/before.jpg", alt: "Before editing" }}
 *   afterImage={{ src: "/images/after.jpg", alt: "After editing" }}
 *   beforeLabel="Original"
 *   afterLabel="Enhanced"
 * />
 * ```
 */
export default function BeforeAfterImageClient({
  beforeImage,
  afterImage,
  className,
  beforeLabel = "Before",
  afterLabel = "After"
}: BeforeAfterImageProps): JSX.Element | null {
  // State for divider position (0-100)
  const [position, setPosition] = useState(50)
  const [isDragging, setIsDragging] = useState(false)

  // Hooks must be called unconditionally at the top of the component
  useEffect((): (() => void) => {
    const handleMouseUpGlobal = (): void => {
      setIsDragging(false)
    }

    window.addEventListener('mouseup', handleMouseUpGlobal)
    window.addEventListener('touchend', handleMouseUpGlobal)

    return (): void => {
      window.removeEventListener('mouseup', handleMouseUpGlobal)
      window.removeEventListener('touchend', handleMouseUpGlobal)
    }
  }, [])

  // Guard against missing images
  if (!beforeImage?.src || !afterImage?.src) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('BeforeAfterImage: Missing source for before or after image')
    }
    return null
  }

  // Ensure valid dimensions for both images
  const width = afterImage.width ?? beforeImage.width ?? 1200
  const height = afterImage.height ?? beforeImage.height ?? 630
  
  // Make sure we have valid positive numbers
  const validWidth = width > 0 ? width : 1200
  const validHeight = height > 0 ? height : 630
  
  // Calculate aspect ratio
  const aspectRatio = `${validWidth}/${validHeight}`

  const handleMouseDown = (): void => {
    setIsDragging(true)
  }

  const handleMouseUp = (): void => {
    setIsDragging(false)
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!isDragging) return

    const rect = e.currentTarget.getBoundingClientRect()
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width))
    const newPosition = Math.max(0, Math.min(100, (x / rect.width) * 100))
    
    setPosition(newPosition)
  }

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>): void => {
    if (!isDragging || !e.touches[0]) return

    const rect = e.currentTarget.getBoundingClientRect()
    const x = Math.max(0, Math.min(e.touches[0].clientX - rect.left, rect.width))
    const newPosition = Math.max(0, Math.min(100, (x / rect.width) * 100))
    
    setPosition(newPosition)
  }

  return (
    <div className={cn('my-8', className)}>
      <div 
        className="relative rounded-xl overflow-hidden cursor-col-resize"
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchMove={handleTouchMove}
        onTouchStart={handleMouseDown}
        onTouchEnd={handleMouseUp}
      >
        <div 
          className="relative"
          style={{ 
            aspectRatio,
            minHeight: '200px'
          }}
        >
          {/* After image (full width) */}
          <Image
            src={afterImage.src}
            alt={afterImage.alt}
            width={afterImage.width ?? 1200}
            height={afterImage.height ?? 630}
            priority={afterImage.priority}
            className="object-cover w-full h-full"
          />
          
          {/* Before image (clipped) */}
          <div 
            className="absolute top-0 left-0 h-full overflow-hidden"
            style={{ width: `${position}%` }}
          >
            <Image
              src={beforeImage.src}
              alt={beforeImage.alt}
              width={beforeImage.width ?? 1200}
              height={beforeImage.height ?? 630}
              priority={beforeImage.priority}
              className="object-cover h-full"
              style={{ width: `${100 / (position / 100)}%` }}
            />
          </div>
          
          {/* Divider */}
          <div 
            className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-col-resize"
            style={{ left: `${position}%` }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleMouseDown}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center">
              <div className="w-0.5 h-4 bg-gray-400 mx-0.5" />
              <div className="w-0.5 h-4 bg-gray-400 mx-0.5" />
            </div>
          </div>
          
          {/* Labels */}
          <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
            {beforeLabel}
          </div>
          <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
            {afterLabel}
          </div>
        </div>

      </div>
      
      {(beforeImage.caption ?? afterImage.caption) && (
        <div className="mt-3 text-center text-sm font-medium text-gray-600 dark:text-gray-400 italic">
          {beforeImage.caption ?? afterImage.caption}
        </div>
      )}
    </div>
  )
}
